import { SAZEO_DOMAINS, buildIntranetUrl } from '@sazeo/shared';

/**
 * Tenant-slug resolution.
 *
 * Production (canonical): `{slug}.app.sazeo.io`
 *   - tbc.app.sazeo.io/dashboard → slug = 'tbc'
 *   - app.sazeo.io/              → slug = null (auth broker)
 *   Custom domains also supported: agrohub.sazeo.io → slug = 'agrohub'
 *
 * Dev: {slug}.localhost:3003  or  ?tenant=slug override
 *
 * Path-based fallback (`resolveTenantFromPath`) is available for dev/testing
 * but is NOT the production routing strategy.
 */

// Top-level paths that are NOT tenant slugs
const RESERVED_PATHS = new Set([
  'login',
  'signup',
  'pricing',
  'about',
  'admin',
  'api',
  'auth',
  'health',
  'invite',
  'reset',
  '',
]);

/**
 * Resolve tenant slug from URL pathname.
 * First path segment that is not reserved = tenant slug.
 */
export function resolveTenantFromPath(pathname: string): string | null {
  const first = pathname.split('/').filter(Boolean)[0] ?? '';
  if (RESERVED_PATHS.has(first.toLowerCase())) return null;
  return first.toLowerCase() || null;
}

export interface TenantHostResolution {
  slug: string | null;
  isDev: boolean;
}

const APEX_DOMAIN = SAZEO_DOMAINS.intranet;
const DEV_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

/**
 * Strip the port from a `host` header ("localhost:3003" → "localhost").
 * Handles IPv6 bracket notation.
 */
function stripPort(host: string): string {
  if (host.startsWith('[')) {
    // IPv6 literal like "[::1]:3003"
    const end = host.indexOf(']');
    if (end >= 0) return host.slice(1, end);
  }
  const idx = host.lastIndexOf(':');
  if (idx <= 0) return host;
  // Only treat as port separator if what follows is numeric.
  const tail = host.slice(idx + 1);
  return /^\d+$/.test(tail) ? host.slice(0, idx) : host;
}

function isDevHost(hostname: string): boolean {
  if (DEV_HOSTS.has(hostname)) return true;
  // Bare `.localhost` subdomains (RFC 6761)
  return hostname.endsWith('.localhost');
}

/**
 * Parse a hostname (with optional port) into `{ slug, isDev }`.
 *
 * Callers usually pass `window.location.host`; tests pass literal strings.
 * In dev, a `?tenant=...` query parameter on `searchOverride` wins over
 * whatever the hostname implies (useful when `*.localhost` DNS isn't set up).
 */
export function resolveTenantFromHost(
  hostHeader: string,
  searchOverride?: string,
  pathnameOverride?: string,
): TenantHostResolution {
  const hostname = stripPort(hostHeader).toLowerCase();
  const isDev = isDevHost(hostname);

  // Path-based resolution is primary — works on any domain (sazeo.io/tbc)
  if (pathnameOverride) {
    const fromPath = resolveTenantFromPath(pathnameOverride);
    if (fromPath) return { slug: fromPath, isDev };
  }

  if (isDev) {
    const queryOverride = searchOverride ? readTenantQuery(searchOverride) : null;
    if (queryOverride) return { slug: queryOverride, isDev: true };

    if (hostname === 'localhost' || DEV_HOSTS.has(hostname)) {
      return { slug: null, isDev: true };
    }
    if (hostname.endsWith('.localhost')) {
      const left = hostname.slice(0, -'.localhost'.length);
      const slug = left.split('.')[0] || null;
      return { slug, isDev: true };
    }
    return { slug: null, isDev: true };
  }

  // Production path.
  if (hostname === APEX_DOMAIN) {
    return { slug: null, isDev: false };
  }
  if (hostname.endsWith(`.${APEX_DOMAIN}`)) {
    const left = hostname.slice(0, -(APEX_DOMAIN.length + 1));
    // Leftmost label is the tenant slug. Nested subdomains aren't supported
    // but we accept the first label to keep the flow deterministic.
    const slug = left.split('.')[0] || null;
    return { slug, isDev: false };
  }
  // Unknown host — treat like apex (caller decides what to do).
  return { slug: null, isDev: false };
}

function readTenantQuery(search: string): string | null {
  const q = search.startsWith('?') ? search.slice(1) : search;
  for (const part of q.split('&')) {
    const [rawKey, rawVal] = part.split('=');
    if (rawKey === 'tenant' && rawVal) {
      return decodeURIComponent(rawVal).toLowerCase() || null;
    }
  }
  return null;
}

/**
 * Build the URL to redirect the user to when they need to sign in or pick a
 * different workspace. Preserves the current location in a `redirect` query
 * param so the broker can return them to this exact page.
 */
export function getTenantLoginRedirectUrl(
  currentHref: string = typeof window === 'undefined' ? '' : window.location.href,
  isDev: boolean = resolveTenantFromHost(
    typeof window === 'undefined' ? APEX_DOMAIN : window.location.host,
  ).isDev,
): string {
  const target = buildIntranetUrl('/login', {
    isDev,
    protocol: isDev ? 'http' : 'https',
  });
  if (!currentHref) return target;
  const sep = target.includes('?') ? '&' : '?';
  return `${target}${sep}redirect=${encodeURIComponent(currentHref)}`;
}

/**
 * Build the URL to redirect the user to the workspace chooser (e.g. when
 * they're signed in but not a member of the tenant matching the current
 * subdomain).
 */
export function getWorkspaceChooserUrl(
  isDev: boolean = resolveTenantFromHost(
    typeof window === 'undefined' ? APEX_DOMAIN : window.location.host,
  ).isDev,
): string {
  return buildIntranetUrl('/workspace-chooser', {
    isDev,
    protocol: isDev ? 'http' : 'https',
  });
}
